import { RealtimeClient } from '@supabase/realtime-js';
import type { ChatRealtimeResponse } from '@/core/generated/api';
import { getErrorMessage } from '@/shared/lib/errors';
import type { ChatDialogRealtimeCallbacks, ChatDialogTransport } from '../model/types';
import { chatDialogService } from './chatDialogService';
import { chatDialogRealtimeEventSchema } from '../model/realtimeSchema';

/** Подготавливает адрес Realtime для стандартного или tenant-маршрута Supabase */
const createRealtimeEndpoint = (url: string): string => {
    const endpoint = new URL(url);
    const path = endpoint.pathname.replace(/\/+$/, '');
    const hasRealtimeVersion = /\/realtime(?:\/[^/]+)?\/v1$/.test(path);

    endpoint.pathname = hasRealtimeVersion
        ? path
        : `${path}/realtime/v1`;

    return endpoint.toString().replace(/\/$/, '');
};

/** Подключает обновления переписки через Supabase Realtime */
export const connectChatDialogRealtime = (
    callbacks: ChatDialogRealtimeCallbacks,
    service: ChatDialogTransport = chatDialogService,
): (() => void) => {
    let disposed = false;
    let client: RealtimeClient | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let access: ChatRealtimeResponse | null = null;
    let renewAfter = 0;

    /** Закрывает соединение с обновлениями */
    const disconnect = (): void => {
        const previous = client;
        client = null;

        if (previous) {
            void previous.removeAllChannels()
                .catch(() => undefined)
                .finally(() => previous.disconnect());
        }
    };

    /** Показывает недоступность соединения */
    const fail = (error: unknown): void => {
        if (disposed) return;
        disconnect();
        callbacks.onState('reconnecting');
        callbacks.onError(getErrorMessage(error, 'Не удалось подключить обновления переписки'));

        if (retryTimer === null) {
            retryTimer = setTimeout(() => {
                retryTimer = null;
                void start();
            }, 5000);
        }
    };

    /** Получает действующее разрешение на обновления */
    const getToken = async (): Promise<string | null> => {
        if (disposed) return null;
        if (access?.accessToken && Date.now() < renewAfter) return access.accessToken;

        try {
            const next = await service.getRealtime();

            if (disposed) return null;

            callbacks.onAvailability?.(next.isAvailable ?? true);

            if (!next.isEnabled || !next.accessToken) {
                disconnect();
                callbacks.onState('disabled');

                return null;
            }

            access = next;
            renewAfter = Date.now() + 60000;

            return next.accessToken;
        } catch (error: unknown) {
            fail(error);

            return null;
        }
    };

    /** Открывает подписку на переписку */
    const start = async (): Promise<void> => {
        if (disposed) return;
        callbacks.onState('connecting');

        try {
            access = await service.getRealtime();
            renewAfter = Date.now() + 60000;

            if (disposed) return;

            callbacks.onAvailability?.(access.isAvailable ?? true);

            if (!access.isEnabled) {
                callbacks.onState('disabled');

                return;
            }

            const url = access.url ?? '';
            const publicApiKey = access.publicApiKey ?? '';
            const topic = access.topic ?? '';

            const incomplete = !url || !publicApiKey || !topic || !access.accessToken
                || !access.expiresAt || !Number.isFinite(Date.parse(access.expiresAt));

            if (incomplete) throw new Error('Не настроен доступ к обновлениям переписки');

            const realtime = new RealtimeClient(createRealtimeEndpoint(url), {
                params: { apikey: publicApiKey },
                accessToken: getToken,
            });

            client = realtime;
            await realtime.setAuth();

            if (disposed || client !== realtime) return;

            realtime.channel(topic, { config: { private: true } })
                .on('broadcast', { event: 'operator-pins-changed' }, () => {
                    if (!disposed && client === realtime) callbacks.onPinsChanged?.();
                })
                .on('broadcast', { event: 'changed' }, ({ payload }: { payload: unknown }) => {
                    if (disposed || client !== realtime) return;
                    const event = chatDialogRealtimeEventSchema.safeParse(payload);

                    if (!event.success) {
                        fail(new Error('Получено некорректное обновление переписки'));

                        return;
                    }

                    callbacks.onEvent(event.data);
                })
                .subscribe((status) => {
                    if (disposed || client !== realtime) return;

                    if (status === 'SUBSCRIBED') {
                        callbacks.onState('connected');
                        callbacks.onReady();
                    } else {
                        callbacks.onState('reconnecting');

                        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
                            callbacks.onError('Обновления переписки недоступны. Переподключаемся…');
                        }
                    }
                });
        } catch (error: unknown) {
            fail(error);
        }
    };

    void start();

    return () => {
        disposed = true;

        if (retryTimer !== null) clearTimeout(retryTimer);
        disconnect();
    };
};
